import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';

export type ModuleStatus = 'ACTIVE' | 'BETA' | 'COMING_SOON' | 'HIDDEN' | 'DISABLED';

/** Item de menu vindo do catálogo: apresentação (do SSM) + acesso do usuário. */
export interface MenuItem {
  readonly key: string;
  readonly byname: string;
  readonly icon: string;
  readonly route: string;
  readonly section: string;
  readonly order: number;
  readonly status: ModuleStatus;
  readonly accessible: boolean;
}

interface MeMenuResponse {
  readonly items: readonly MenuItem[];
}

/**
 * Itens de menu do usuário logado, vindos do catálogo (`GET /me/menu`). É a fonte
 * data-driven da sidebar: estrutura, ícone, rótulo, seção, ordem e status vêm do
 * catálogo (declarado em código no SSM); o acesso de cada item é resolvido por
 * `module:action` no backend. Aberto a qualquer autenticado (ao contrário de
 * `/permissions/catalog`, que é da administração).
 */
@Injectable({ providedIn: 'root' })
export class MenuService {
  private readonly http = inject(HttpClient);
  private readonly _items = signal<readonly MenuItem[]>([]);
  private loaded = false;

  readonly items = this._items.asReadonly();

  /** Carrega os itens do menu uma única vez (idempotente). */
  async ensureLoaded(): Promise<void> {
    if (this.loaded) {
      return;
    }
    await this.reload();
  }

  /** Recarrega do backend (ex.: após mudança de status de módulo na administração). */
  async reload(): Promise<void> {
    try {
      const response = await firstValueFrom(this.http.get<MeMenuResponse>(`${environment.apiUrl}/me/menu`));
      this._items.set(response?.items ?? []);
      this.loaded = true;
    } catch {
      this._items.set([]);
    }
  }

  /** Esquece o menu (no logout). */
  clear(): void {
    this._items.set([]);
    this.loaded = false;
  }
}
